import type { MouseEvent, PropsWithChildren } from 'react';

import { Flex } from '@/components/Flex';
import { useListingState } from '@/hooks/useListingState';
import { listingActions } from '@/states/listingState';

import { useListingFiltersProvider } from '../../ListingFiltersProviderContext';
import { FiltersCounter, FiltersCounterForSearchView } from '../FiltersCounter';

import * as S from '../FiltersModal/FiltersModal.styles';

interface FilterAccordionProps {
  attribute?: string;
  render?: (isVisible: boolean) => JSX.Element;
  summary: string;
}

export const FilterAccordion = ({
  attribute,
  children,
  render,
  summary,
}: PropsWithChildren<FilterAccordionProps>) => {
  const { modalState } = useListingState();
  // When there is no attribute (e.g. SortBy accordion), leave open uncontrolled
  const isOpen = attribute ? modalState.openedTab === attribute : undefined;
  const { listingFiltersType } = useListingFiltersProvider();

  const handleSummaryClick = attribute
    ? (e: MouseEvent<HTMLElement>) => {
        e.preventDefault();
        listingActions.updateModalState({
          openedTab: modalState.openedTab === attribute ? null : attribute,
        });
      }
    : undefined;

  return (
    <S.Details data-test-id='FiltersModalFilterAccordion' open={isOpen}>
      <S.Summary
        data-test-id='FiltersModalSummary'
        onClick={handleSummaryClick}
      >
        <Flex gridGap={1}>
          {summary.toUpperCase()}{' '}
          {attribute &&
            (listingFiltersType === 'PLP' ? (
              <FiltersCounter
                attribute={attribute}
                data-test-id={`FiltersModal${attribute}Counter`}
              />
            ) : (
              <FiltersCounterForSearchView
                attribute={attribute}
                data-test-id={`FiltersModal${attribute}Counter`}
              />
            ))}
        </Flex>

        <S.ChevronIcon name='chevronDown' />
      </S.Summary>

      <S.DetailsBody data-test-id='FiltersModalDetailsBody'>
        {children} {render?.(isOpen ?? false)}
      </S.DetailsBody>
    </S.Details>
  );
};
